import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Eye, Trash, Pencil, Link } from 'lucide-react';
import { RadioGroup } from '@/components/ui/radio-group';
import { ReferralTemplateConfig } from '@/utils/config';
import { Badge } from '@/components/ui/badge';
import { CampaignIcon } from '@/components/icons/CustomIcons';

// Remove the old CampaignIcon component as we're now importing it
interface TemplateListProps {
  templates: ReferralTemplateConfig[];
  onEdit: (template: ReferralTemplateConfig) => void;
  onDelete: (id: string) => void;
  onPreview: (template: string) => void;
  onSetDefault: (id: string) => void;
  getUrlNameById: (id: string) => string;
  getSourceText: (template: ReferralTemplateConfig) => string;
}

const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  onEdit,
  onDelete,
  onPreview,
  onSetDefault,
  getUrlNameById,
  getSourceText,
}) => {
  return (
    <RadioGroup>
      {templates.map((template) => (
        <Card key={template.id} className={template.isDefault ? 'border-primary' : ''}>
          <CardHeader className="py-4 px-6">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  {template.name}
                  {template.campaignId && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      <CampaignIcon />
                      Campaign
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {getSourceText(template)}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => onPreview(template.messageTemplate || template.template)}
                  title="Preview template"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onEdit(template)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  disabled={template.isDefault} 
                  onClick={() => onDelete(template.id)}
                >
                  <Trash className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="py-2 px-6">
            <div className="text-sm text-muted-foreground">
              {template.messageTemplate || template.template}
            </div>
          </CardContent>
          <div className="flex justify-between border-t p-4">
            <div className="text-sm text-muted-foreground">
              {/* Safe way to display creation date without accessing createdAt directly */}
              Created: Unknown date
            </div>
            {template.isDefault ? (
              <div className="flex items-center text-sm text-primary">
                Default Template
              </div>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onSetDefault(template.id)}
              >
                Set as Default
              </Button>
            )}
          </div>
        </Card>
      ))}
    </RadioGroup>
  );
};

export default TemplateList;
